
import React, { useState, useEffect } from 'react';
import { Question } from '@/types/survey';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/contexts/LanguageContext';
import { AlertCircle, HelpCircle, Lightbulb, CheckCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { motion } from 'framer-motion';
import { Progress } from '@/components/ui/progress';

interface TextInputQuestionProps {
  question: Question;
  answer?: string;
  onChange: (value: string) => void;
}

const TextInputQuestion: React.FC<TextInputQuestionProps> = ({
  question,
  answer = '',
  onChange,
}) => {
  const { t, language } = useLanguage();
  const [wordCount, setWordCount] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  
  useEffect(() => {
    // Safely handle the word count calculation
    if (answer && typeof answer === 'string') {
      const words = answer.trim().split(/\s+/);
      setWordCount(words.length === 1 && words[0] === '' ? 0 : words.length);
    } else {
      setWordCount(0);
    }
  }, [answer]);
  
  // Skip word count check for Chinese language
  const isMinWordsMet = language === 'zh' || (question.minWords ? wordCount >= question.minWords : true);
  
  // Calculate progress percentage for word count
  const progressPercentage = question.minWords ? Math.min(100, (wordCount / question.minWords) * 100) : 0;
  
  return (
    <motion.div 
      className="space-y-4"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {question.explanation && (
        <motion.div 
          className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100 shadow-sm"
          whileHover={{ y: -2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}
          transition={{ duration: 0.2 }}
        >
          <div className="flex">
            <Lightbulb className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-blue-700 mb-1">{t('survey.text.explanation') || "Explanation"}</h4>
              <p className="text-sm text-blue-600">{question.explanation}</p>
            </div>
          </div>
        </motion.div>
      )}
      
      <div className={`relative backdrop-blur-sm bg-white/70 rounded-xl border ${isFocused ? 'border-primary shadow-md ring-1 ring-primary/20' : 'border-gray-200'} transition-all duration-300`}>
        <Textarea
          placeholder={question.tooltip || t('survey.text.defaultPlaceholder') || "Enter your answer here..."}
          value={answer}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className={`min-h-[180px] p-4 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 resize-y rounded-xl text-base`}
        />
      </div>
      
      {/* Only show word count for non-Chinese language */}
      {language !== 'zh' && question.minWords && (
        <div className="space-y-2">
          <div className="flex justify-between items-center text-sm">
            <div className={`flex items-center ${isMinWordsMet ? 'text-gray-600' : 'text-amber-600'}`}>
              {!isMinWordsMet && <AlertCircle className="h-4 w-4 mr-1" />}
              <span>{t('survey.text.current') || "Current word count"}: {wordCount}</span>
            </div>
            
            <div className={`${isMinWordsMet ? 'text-green-600 flex items-center' : 'text-amber-600'}`}>
              <span>{t('survey.text.instructions') || "Minimum words"}: {question.minWords}</span>
              {isMinWordsMet && <CheckCircle className="h-4 w-4 ml-1 animate-fade-in" />}
            </div>
          </div>
          
          <Progress value={progressPercentage} className="h-2" 
            style={{
              background: 'rgba(226, 232, 240, 0.5)',
              backgroundImage: `linear-gradient(to right, #60a5fa, #818cf8)`, 
              backgroundSize: `${progressPercentage}% 100%`,
              backgroundRepeat: 'no-repeat'
            }} 
          />
        </div>
      )}
      
      {question.example && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="text-sm text-blue-500 cursor-help flex items-center">
                <HelpCircle className="h-4 w-4 mr-1" /> {t('survey.text.seeExample') || "See example"}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-sm">{question.example}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="text-sm text-gray-500 flex items-center cursor-help">
              <Lightbulb className="h-4 w-4 text-amber-500 mr-1" /> 
              <span>{t('survey.text.guidance') || "Writing guidance"}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-sm">{t('survey.text.guidanceTooltip') || "Provide specific examples and be as detailed as possible in your response."}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </motion.div>
  );
};

export default TextInputQuestion;
